


















































































































































































































































































import axios from 'axios';
import Component from 'vue-class-component';

import { baConfig } from '@/../config';
import { Org } from '@/api-svc-types';
import { BaseVue } from '@/BaseVue';
import AppLink from '@/components/AppLink.vue';
import UiButton from '@/components/ui/UiButton.vue';
import UiCheckbox from '@/components/ui/UiCheckbox.vue';
import UiLoading from '@/components/ui/UiLoading.vue';
import UiPageHeader from '@/components/ui/UiPageHeader.vue';
import UiSelect2 from '@/components/ui/UiSelect2.vue';
import UiTextEdit from '@/components/ui/UiTextEdit.vue';
import { MUT_SNACKBAR } from '@/store';
import { getClusterAwareBaseUrl } from '@/utils/clusterAwareUrl';
import { getEndpointUrl } from '@/utils/endpointUrlUtil';
import { requestParentToNavigate } from '@/utils/iframeMessageRequester';

import { RateTable } from './types/rateTableTypes';

// Type definitions for form data structures

@Component({
  components: {
    UiPageHeader,
    UiButton,
    UiTextEdit,
    UiSelect2,
    UiLoading,
    UiCheckbox,
    AppLink,
  },
})
export default class PricingContextDetails extends BaseVue {
  public baseUrl = process.env.VUE_APP_API_URL;

  public isNew = false;
  public pricingContextId = '';
  public formValidated = false;
  public isSaving = false;
  public isLoading = false;
  public isLoadingRateTables = false;
  public rateTables: RateTable[] = [];

  public get pricingSources() {
    const sources = [
      { label: 'Binance', id: 'binance' },
      { label: 'Coinbase', id: 'coinbase' },
      { label: 'Coin Gecko', id: 'coin-gecko' },
      { label: 'Crypto Compare', id: 'crypto-compare' },
      { label: 'Coin Market Cap', id: 'coin-market-cap' },
    ];

    if (this.checkFeatureFlag('kaiko-pricing', this.features)) {
      sources.push(
        ...[
          { label: 'Kaiko Direct', id: 'kaiko-direct' },
          { label: 'Kaiko Synthetic', id: 'kaiko-synthetic' },
          { label: 'Kaiko Principal Market', id: 'kaiko-principal-market' },
          { label: 'Kaiko State Price', id: 'kaiko-state-price' },
        ]
      );
    }

    return sources;
  }

  blankContextForm = {
    id: '',
    description: '',
    resolution: '1h',
    methodology: 'close',
    isDisabled: false,
    action: {
      type: 'price-override',
      price: undefined,
      sourceOrdering: Array.from([
        {
          coin: '',
          type: 'binance',
          exchange: 'None',
          fiatToCoinTickerMapping: {},
          conversionPricingDirective: {
            source: { type: undefined },
            type: undefined,
          },
          rateTableId: undefined,
        },
      ]),
    },
  };

  public pricingContextForm = { ...this.blankContextForm };

  public coinMappingExchanges: string[] = [
    'Binance',
    'BinanceUS',
    'HuobiGlobal',
    'Poloniex',
    'Kucoin',
    'Bitvavo',
    'Bit.com',
    'MEXC',
  ];

  async mounted() {
    const pricingContextId = this.$route.params.id;
    if (pricingContextId === 'new') {
      this.isNew = true;
    } else {
      this.pricingContextId = pricingContextId;
      await this.loadExistingPricingContext(pricingContextId);
    }
  }

  private async loadExistingPricingContext(contextId: string) {
    try {
      this.isLoading = true;
      const resp = await axios.get(`${this.baseUrl}/orgs/${this.$store.state.currentOrg.id}/pricing-contexts`, {
        withCredentials: true,
      });

      const pricingContexts = resp.data;
      const existingContext = pricingContexts.find((context: any) => context.id === contextId);

      if (existingContext) {
        this.populateFormWithExistingData(existingContext);
      } else {
        console.error(`Pricing context with ID ${contextId} not found`);
        this.$store.commit(MUT_SNACKBAR, {
          message: `Pricing context with ID ${contextId} not found`,
          color: 'error',
        });
      }
    } catch (error) {
      console.error('Error loading pricing context:', error);
      this.$store.commit(MUT_SNACKBAR, {
        message: 'Error loading pricing context',
        color: 'error',
      });
    } finally {
      this.isLoading = false;
    }
  }

  private populateFormWithExistingData(context: any) {
    this.pricingContextForm = {
      id: context.id || '',
      description: context.description || '',
      resolution: context.resolution || '1h',
      methodology: context.methodology || 'close',
      isDisabled: context.isDisabled || false,
      action: context.action || { ...this.blankContextForm.action },
    };

    if (!this.pricingContextForm.action.sourceOrdering) {
      this.pricingContextForm.action.sourceOrdering = [...this.blankContextForm.action.sourceOrdering];
    }
  }

  public requestParentToNavigate(path: string) {
    requestParentToNavigate(path);
  }

  public onActionTypeChange(actionType: string) {
    if (actionType === 'rate-table-lookup' && !this.rateTables?.length) {
      this.loadRateTables();
    }
  }

  public onAddNewSource() {
    this.pricingContextForm.action.sourceOrdering.push({
      type: 'binance',
      exchange: 'Binance',
      coin: '',
      fiatToCoinTickerMapping: {},
      conversionPricingDirective: { type: undefined, source: { type: undefined } },
      rateTableId: undefined,
    });
  }

  async loadRateTables() {
    if (this.isLoadingRateTables) return;
    this.isLoadingRateTables = true;

    try {
      const svcURL = getClusterAwareBaseUrl(baConfig.api2Url || '');
      const orgId = this.$store.state.currentOrg.id;
      const endpointURL = getEndpointUrl(svcURL, ['v2', 'orgs', orgId, 'rate-tables']);
      const resp = await axios.get<{ rateTables: RateTable[] }>(endpointURL, {
        withCredentials: true,
      });

      this.isLoadingRateTables = false;

      if (resp.status === 200) {
        this.rateTables = (resp.data?.rateTables ?? []).sort((a, b) =>
          (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' })
        );
      } else {
        this.showErrorSnackbar('Could not fetch rate tables');
      }
    } catch (error) {
      console.error(error);
      this.isLoadingRateTables = false;
      this.showErrorSnackbar('Unexpected error while loading rate tables');
    }
  }

  public getPriceOverrideAction(action: any) {
    const newAction: any = { type: 'price-override' };
    newAction.price = Number(action.price);
    return newAction;
  }

  public getRateTableLookupAction(org: Org, action: any) {
    const newAction: any = { type: 'ordered-source-search' };
    newAction.sourceOrdering = [
      {
        type: 'rate-table',
        rateTableId: action.sourceOrdering[0].rateTableId,
        sourceLocator: 'org', // Hard coded as it is the only option for now, might change
        sourceLocatorId: org.id,
      },
    ];
    return newAction;
  }

  public getOrderedSourceSearchAction(org: Org, action: any) {
    const newAction: any = { type: 'ordered-source-search' };
    const baseCurrency = org.baseCurrency;
    newAction.sourceOrdering = action.sourceOrdering.map((order: any) => {
      switch (order.type) {
        case 'binance':
          if (!baseCurrency) {
            throw new Error(
              'Unable to create new ordered source search action for Binance. Org base currency null or undefined'
            );
          }
          return {
            type: order.type,
            fiatToCoinTickerMapping: {
              [baseCurrency]: order.coin,
            },
          };
        case 'crypto-compare':
          if (!baseCurrency) {
            throw new Error(
              'Unable to create new ordered source search action for CryptoCompare. Org base currency null or undefined'
            );
          }
          return {
            type: order.type,
            exchange: order.exchange,
            fiatToCoinTickerMapping: {
              [baseCurrency]: order.coin,
            },
          };
        default:
          return {
            type: order.type,
          };
      }
    });
    return newAction;
  }

  public getAction(action: any, org: Org) {
    switch (action.type) {
      case 'price-override':
        return this.getPriceOverrideAction(action);
      case 'ordered-source-search':
        return this.getOrderedSourceSearchAction(org, action);
      case 'rate-table-lookup':
        return this.getRateTableLookupAction(org, action);
      default:
        throw new Error('Unsupported action type');
    }
  }

  public async onSavePricingContext() {
    this.formValidated = true;
    if (!this.isFormValid) {
      this.$store.commit(MUT_SNACKBAR, {
        color: 'warning',
        message: 'Form invalid. Please check the highlighted fields and try again.',
      });
      return;
    }

    try {
      this.isSaving = true;
      const org: Org = this.$store.state.currentOrg;
      const action = this.getAction(this.pricingContextForm.action, org);

      let resp;
      if (this.isNew) {
        // Create new pricing context
        const payload = {
          id: this.pricingContextForm.id || '',
          description: this.pricingContextForm.description,
          resolution: this.pricingContextForm.resolution,
          methodology: this.pricingContextForm.methodology,
          isDisabled: this.pricingContextForm.isDisabled,
          action,
          rules: [],
        };

        resp = await axios.post(`${this.baseUrl}/orgs/${this.$store.state.currentOrg.id}/pricing-contexts`, payload, {
          withCredentials: true,
        });
      } else {
        // Update existing pricing context - call both PUT and PATCH
        const putPayload = {
          action,
          description: this.pricingContextForm.description,
          method: this.pricingContextForm.methodology, // methodology -> method for PUT
          resolution: this.pricingContextForm.resolution,
        };

        const patchPayload = {
          isDisabled: this.pricingContextForm.isDisabled,
        };

        // Call PUT endpoint for main fields
        await axios.put(
          `${this.baseUrl}/orgs/${this.$store.state.currentOrg.id}/pricing-contexts/${this.pricingContextId}`,
          putPayload,
          {
            withCredentials: true,
          }
        );

        // Call PATCH endpoint for isDisabled field
        await axios.patch(
          `${this.baseUrl}/orgs/${this.$store.state.currentOrg.id}/pricing-contexts/${this.pricingContextId}`,
          patchPayload,
          {
            withCredentials: true,
          }
        );
      }

      this.showSnackbar('success', 'Pricing context saved successfully');
      this.requestParentToNavigate('/pricing/context');
    } catch (e) {
      this.$store.commit(MUT_SNACKBAR, {
        color: 'error',
        message: 'Problem saving pricing context: ' + (e instanceof Error ? e.message : 'Unknown error'),
      });
    } finally {
      this.isSaving = false;
    }
  }

  public get isFormValid() {
    if (
      !this.pricingContextForm.description ||
      !this.pricingContextForm.resolution ||
      !this.pricingContextForm.methodology ||
      !this.pricingContextForm.action.type
    ) {
      return false;
    }

    // price override validation
    if (
      this.pricingContextForm.action.type === 'price-override' &&
      (!this.pricingContextForm.action.price || this.pricingContextForm.action.price < 0)
    ) {
      return false;
    }

    // ordered source search validation
    if (this.pricingContextForm.action.type === 'ordered-source-search') {
      for (const order of this.pricingContextForm.action.sourceOrdering) {
        if (!order.type) return false;
        if (order.type === 'crypto-compare') {
          //  we can remove the following check once we implement minute resolution
          const currentlyUnsupportedResolutions = ['minute', 'minute-5', 'minute-10', 'minute-15'];
          if (currentlyUnsupportedResolutions.includes(this.pricingContextForm.resolution)) {
            return false;
          }
          if (!order.exchange) return false;
          if (this.coinMappingExchanges.includes(order.exchange) && !order.coin) return false;
        }
        if (order.type === 'binance') {
          if (!order.coin) {
            console.log('fail binance');
            return false;
          }
        }
      }
    }

    // rate table lookup validation
    if (
      this.pricingContextForm.action.type === 'rate-table-lookup' &&
      this.pricingContextForm.action.sourceOrdering.some((s) => !s?.rateTableId)
    ) {
      return false;
    }

    return true;
  }

  showSnackbar(action: string, message: string) {
    this.$store.commit(MUT_SNACKBAR, {
      color: action,
      message,
    });
  }

  showErrorSnackbar(message: string) {
    this.$store.commit(MUT_SNACKBAR, {
      color: 'error',
      message,
    });
  }
}
